import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { PageContentWrapper } from '@unwrittenmedia/simple-components'
import SimplePageHeader2 from '@/components/SimplePageHeader2'
import clsx from 'clsx';
import { useCreatePlace } from '@/hooks/useCreatePlace'
import { useGQL_teamAdminsById } from '@/contexts/graphql-and-swr-apis'
import type { TAPIPlace } from '@/contexts/graphql-and-swr-apis'
import { createAuthGuard } from '@/lib/authGuard'

type NewPlaceSearch = {
  team: string
}

export const Route = createFileRoute('/places/new')({
  beforeLoad: createAuthGuard(),
  component: NewPlacePage,
  validateSearch: (search: Record<string, unknown>): NewPlaceSearch => ({
    team: (search.team as string) || '',
  }),
})

const inputBase = 'w-full rounded-lg border border-gray-300 dark:border-mist-700 bg-white dark:bg-mist-950 px-4 py-2 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2'
const labelBase = 'block text-sm font-medium text-gray-700 dark:text-mist-400 mb-1'

function NewPlacePage() {
  const navigate = useNavigate()
  const { team: teamId } = Route.useSearch()
  const { team } = useGQL_teamAdminsById(teamId, { teamInfo: true })
  const [placeName, setPlaceName] = useState('')
  const [placeCode, setPlaceCode] = useState('')
  const placeCodeValid = placeCode === '' || /^[A-Z0-9-]{6,8}$/.test(placeCode)

  const { createPlace, isLoading } = useCreatePlace({
    onSuccess: (place: TAPIPlace) =>
      navigate({ to: '/team/$teamId/place/$placeId', params: { teamId: place.teamId ?? teamId, placeId: place.id } }),
  })

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!placeName.trim()) return
    try {
      await createPlace({
        teamId,
        name: placeName,
        address: '',
        latitude: 0,
        longitude: 0,
        placeId: placeCode || undefined,
      })
    } catch (err) {
      console.error('Failed to create place:', err)
    }
  }

  return (
    <PageContentWrapper narrow>
      <SimplePageHeader2
        title="Add a New Screen"
        description="Set up a new screen to track and present slides."
      />

      <div className="mt-8 bg-white dark:bg-mist-900 shadow rounded-xl ring-1 ring-gray-200 dark:ring-mist-800 p-8">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <div className="pb-2 border-b border-gray-200 dark:border-mist-800">
              <p className={labelBase}>Team</p>
              <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{team?.name ?? "UNKNOWN TEAM"}</p>
            </div>
          <div>
            <label
              htmlFor="place-name"
              className={labelBase}
            >
              Name
            </label>
            <input
              id="place-name"
              type="text"
              placeholder="e.g. Main Sanctuary"
              value={placeName}
              onChange={(e) => setPlaceName(e.target.value)}
              className={clsx(inputBase, 'focus:ring-indigo-500')}
            />
          </div>

          <div>
            <label
              htmlFor="place-code"
              className={labelBase}
            >
              Vanity Code
            </label>
            <input
              id="place-code"
              type="text"
              placeholder="e.g. NOTES"
              maxLength={8}
              pattern="[A-Z0-9-]{6,8}"
              minLength={6}
              value={placeCode}
              onChange={(e) => setPlaceCode(e.target.value.toUpperCase())}
              className={clsx(inputBase, placeCodeValid ? 'focus:ring-green-500' : 'focus:ring-orange-500')}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-mist-500">
              A short identifier used in URLs. Lowercase letters, numbers, and hyphens only. Must be 6-8 characters long.
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-mist-500">
              Leave blank and we will assign one automatically.
            </p>
          </div>

          <div>
            <label
              htmlFor="place-description"
              className={labelBase}
            >
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              id="place-description"
              rows={3}
              placeholder="A brief description of this place..."
              className={clsx(inputBase, 'focus:ring-indigo-500 resize-none')}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isLoading || !placeName.trim() || !placeCodeValid}
              className="flex-1 rounded-xl bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating...' : 'Create Screen'}
            </button>
            <button
              type="button"
              onClick={() => navigate({ to: '/today' })}
              className="rounded-xl border-2 border-gray-300 dark:border-mist-700 px-6 py-3 text-base font-semibold text-gray-700 dark:text-mist-400 bg-transparent hover:bg-gray-100 dark:hover:bg-mist-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </PageContentWrapper>
  )
}
